import axios from 'axios'
import { push } from 'connected-react-router'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { IDENTITY_API } from 'constants/url'
import { authLoginFormFail, authLoginFormSuccess } from 'store/actions'
import intl from 'react-intl-universal'
import { toast } from 'react-toastify'
import { fromIdentity } from 'store/selectors'
import * as actions from './actions'
import { getTicket, getIdentity } from './selectors'

export function* postSession({ payload }) {
  try {
    const { email: username, password } = payload
    const response = yield call(axios.post, `${IDENTITY_API}/session`, {
      password,
      username: username.toLowerCase()
    })
    const { token } = response.data

    if (token) {
      yield put(push(`/resend-activation-email?token=${token}`))
    } else {
      localStorage.setItem('lastEnteredEmail', username)
      yield put(actions.identityPostSessionSuccess(response.data))
      yield put(actions.identityGetAccount(response.data))
    }
  } catch (error) {
    yield put(authLoginFormFail(error))
    yield put(actions.identityPostSessionFail(error))
  }
}

export function* putAccount({ payload }) {
  const session = localStorage.getItem('session')
  const ticket = yield select(fromIdentity.getTicket)

  try {
    const { data } = yield call(
      axios.put,
      `${IDENTITY_API}/account`,
      {
        ...payload
      },
      {
        headers: {
          authenticateit_identity_ticket: ticket,
          Accept: 'application/json, text/plain, */*'
        }
      }
    )

    localStorage.setItem(
      'session',
      session
        ? JSON.stringify(
            Object.assign(JSON.parse(session), data, {
              account: data
            })
          )
        : JSON.stringify(
            Object.assign(data, {
              account: data
            })
          )
    )

    let passwordUpdate = false
    if (payload.old_password && payload.password) {
      passwordUpdate = true
    }

    yield put(actions.identityPutAccountSuccess({ data, passwordUpdate }))

    toast.success(intl.get('alerts.putAccountSuccess'))
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      toast.error(`${error.response.data.error}`)
    }

    yield put(actions.identityPutAccountFail(error))
  }
}

export function* changeParticipant({ payload }) {
  try {
    const { id, session } = payload
    const state = yield select(getIdentity)
    const { ticket } = state.identity
    const data = { new_participant: id }
    const response = yield call(() =>
      axios.post(`${IDENTITY_API}/session/switch-context`, data, {
        headers: { authenticateit_identity_ticket: ticket }
      })
    )
    let newModules = session.modules
    response.data.participants.forEach(el => {
      if (el.id === response.data.current_participant) {
        newModules = el.modules
      }
    })

    const fullSession = {
      ...session,
      current_participant: response.data.current_participant,
      modules: newModules
    }

    localStorage.setItem('session', JSON.stringify(fullSession))

    let roles = []
    if (response && response.data && response.data.participants && response.data.participants.length) {
      const currParticipant = response.data.participants.filter(
        participant => participant.id === response.data.current_participant
      )

      roles = currParticipant && currParticipant.length && currParticipant[0].roles ? currParticipant[0].roles : []
    }

    yield put(
      actions.identityChangeParticipantSuccess({
        current_participant: response.data.current_participant,
        modules: newModules,
        roles
      })
    )
    yield put(authLoginFormSuccess())
  } catch (error) {
    yield put(actions.identityChangeParticipantFail(error))
  }
}

export function* getAccount({ payload }) {
  const session = localStorage.getItem('session')
  const ticket = yield select(getTicket)
  try {
    const response = yield call(axios.get, `${IDENTITY_API}/account`, {
      headers: {
        authenticateit_identity_ticket: ticket || payload.ticket
      }
    })

    if (payload.upgrade) {
      return
    }

    const systemParticipant =
      response.data &&
      response.data.participants &&
      response.data.participants.find(item => item.participant_type.includes('system'))

    if (systemParticipant) {
      yield put(actions.identityChangeParticipant({ id: systemParticipant.id, session: session || payload }))
      yield put(actions.identityGetAccountSuccess(response.data))
    } else {
      yield put(authLoginFormFail())
      yield put(actions.identityGetAccountFail(intl.get('alerts.systemError')))
    }
  } catch (error) {
    yield put(authLoginFormFail(error))
    yield put(actions.identityGetAccountFail(error))
  }
}

export function* deleteSession({ payload }) {
  const ticket = yield select(getTicket)

  if (!ticket && (!payload || !payload.ticket)) {
    return
  }

  try {
    yield call(axios.delete, `${IDENTITY_API}/session`, {
      headers: {
        authenticateit_identity_ticket: ticket || payload.ticket
      }
    })

    localStorage.removeItem('session')
    yield put(actions.identityDeleteSessionSuccess())
  } catch (error) {
    yield put(actions.identityDeleteSessionFail(error))
  }
}

export function* getSession({ payload }) {
  const session = localStorage.getItem('session')
  const ticket = yield select(getTicket)
  try {
    const response = yield call(axios.get, `${IDENTITY_API}/session`, {
      headers: {
        authenticateit_identity_ticket: ticket || payload.ticket
      }
    })

    localStorage.setItem(
      'session',
      session ? JSON.stringify(Object.assign(JSON.parse(session), response.data)) : JSON.stringify(response.data)
    )

    yield put(actions.identityGetSessionSuccess(JSON.parse(localStorage.getItem('session'))))
    if (payload.isNeedToGetAccount) {
      yield put(actions.identityGetAccount({ ticket: payload.ticket }))
    }
  } catch (error) {
    localStorage.removeItem('session')
    yield put(actions.identityGetSessionFail(error))
  }
}

export default function*() {
  yield takeEvery(actions.IDENTITY_POST_SESSION, postSession)
  yield takeEvery(actions.IDENTITY_GET_ACCOUNT, getAccount)
  yield takeEvery(actions.IDENTITY_CHANGE_PARTICIPANT, changeParticipant)
  yield takeEvery(actions.IDENTITY_DELETE_SESSION, deleteSession)
  yield takeEvery(actions.IDENTITY_GET_SESSION, getSession)
  yield takeEvery(actions.IDENTITY_PUT_ACCOUNT, putAccount)
}
